import { useCallback, useEffect, useRef } from 'react';

import { SessionStorageManager, useApplicationState } from '@atlas/core';

import {
  POSTHOG_CTA,
  POSTHOG_EVENTS,
  POSTHOG_PAGE_REFER,
  POSTHOG_SESSION_KEYS,
} from 'common/constants';
import { getDepositPageRefer } from 'helpers/postHogPageRefer';

import usePostHog from './usePostHog';

const useDepositPostHog = () => {
  const postHog = usePostHog();
  const isDepositStartedRef = useRef(false);
  const isInitializedRef = useRef(false);
  const pageReferRef = useRef(null);
  const storedPageRefer = SessionStorageManager.getItem(POSTHOG_SESSION_KEYS.DEPOSIT_PAGE_REFER);
  const depositPageRefer = getDepositPageRefer(storedPageRefer);
  const isAlternativePaymentPageRefer =
    depositPageRefer === POSTHOG_PAGE_REFER.ALTERNATIVE_PAYMENT_METHOD_POP_UP;

  if (depositPageRefer) {
    SessionStorageManager.setItem(
      POSTHOG_SESSION_KEYS.LAST_DEPOSIT_PAGE_REFER,
      depositPageRefer,
    );
  }

  if (depositPageRefer && (!pageReferRef.current || isAlternativePaymentPageRefer)) {
    pageReferRef.current = depositPageRefer;
  }

  if (!pageReferRef.current) {
    pageReferRef.current =
      SessionStorageManager.getItem(POSTHOG_SESSION_KEYS.LAST_DEPOSIT_PAGE_REFER) || null;
  }

  const {
    account,
    wallet: { detailedBalance: { BONUS = 0, CASH = 0 } = {} },
  } = useApplicationState();

  const getBaseData = useCallback(() => {
    return {
      client_id: account.id,
      page_refer: pageReferRef.current,
      user_bonus_balance: BONUS,
      user_real_balance: CASH,
    };
  }, [account.id, BONUS, CASH]);

  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const initData = {
      ...getBaseData(),
      amount: null,
      bonus_id: null,
      chips_clicked: null,
      cta_clicked: null,
      deposit_leaving_cta_clicked: null,
      deposit_leaving_pop_up_timestamp: null,
      manual_closed_timestamp: null,
      max_amount_clicked: null,
      payment_method: null,
      payment_method_changed: null,
      show_more_clicked: null,
      shown_timestamp: Date.now(),
    };

    postHog.concat(POSTHOG_EVENTS.DEP_POP_UP_SHOWN, initData);
  }, [getBaseData, postHog]);

  useEffect(() => {
    return () => {
      if (!isDepositStartedRef.current) {
        postHog.send(POSTHOG_EVENTS.DEP_POP_UP_SHOWN, { manual_closed_timestamp: Date.now() });
        SessionStorageManager.removeItem(POSTHOG_SESSION_KEYS.DEPOSIT_PAGE_REFER);
      }
    };
  }, []);

  const handleDepositStart = useCallback(
    ({ amount, bonus_id, payment_method }) => {
      const storedDepositPageRefer = SessionStorageManager.getItem(
        POSTHOG_SESSION_KEYS.DEPOSIT_PAGE_REFER,
      );
      const nextDepositPageRefer = getDepositPageRefer(storedDepositPageRefer);

      if (nextDepositPageRefer) {
        pageReferRef.current = nextDepositPageRefer;
        SessionStorageManager.setItem(
          POSTHOG_SESSION_KEYS.LAST_DEPOSIT_PAGE_REFER,
          nextDepositPageRefer,
        );
      }

      const startData = {
        amount,
        bonus_id,
        cta_clicked: POSTHOG_CTA.DEPOSIT,
        payment_method,
      };

      isDepositStartedRef.current = true;
      postHog.concat(POSTHOG_EVENTS.DEP_POP_UP_SHOWN, { ...getBaseData(), ...startData });
    },
    [getBaseData, postHog],
  );

  const handleDepositSuccess = useCallback(
    ({ transaction_id }) => {
      postHog.send(
        POSTHOG_EVENTS.DEP_POP_UP_SHOWN,
        transaction_id != null ? { transaction_id } : undefined,
      );
      SessionStorageManager.removeItem(POSTHOG_SESSION_KEYS.DEPOSIT_PAGE_REFER);
    },
    [postHog],
  );

  const handleChipClick = useCallback(
    amount => {
      postHog.concat(POSTHOG_EVENTS.DEP_POP_UP_SHOWN, { chips_clicked: amount });
    },
    [postHog],
  );

  const handlePaymentMethodChange = useCallback(
    methodName => {
      postHog.concat(POSTHOG_EVENTS.DEP_POP_UP_SHOWN, { payment_method_changed: methodName });
    },
    [postHog],
  );

  const handleDepositLeavingPopupShown = useCallback(() => {
    postHog.concat(POSTHOG_EVENTS.DEP_POP_UP_SHOWN, {
      deposit_leaving_pop_up_timestamp: Date.now(),
    });
  }, [postHog]);

  const handleDepositLeavingCtaClick = useCallback(() => {
    postHog.concat(POSTHOG_EVENTS.DEP_POP_UP_SHOWN, {
      deposit_leaving_cta_clicked: POSTHOG_CTA.DEPOSIT,
    });
  }, [postHog]);

  return {
    handleChipClick,
    handleDepositLeavingCtaClick,
    handleDepositLeavingPopupShown,
    handleDepositStart,
    handleDepositSuccess,
    handlePaymentMethodChange,
  };
};

export default useDepositPostHog;
